import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Link,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SquareIcon from '@mui/icons-material/Square';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { ReactComponent as RefreshIcon } from '../../../../assets/common-assets/refresh.svg';
import { ReactComponent as BlockIcon } from '../../../../assets/common-assets/block.svg';
import { Root, classes } from './utils';
import { lightPalette, theme } from '../../../../theme';
import { getAllFlowData } from '../../../../services/flowConfiguration';
import { getFlowLogsById } from '../../../../services/flowLogs';
import CustomTooltip from '../../utils/CustomTooltip';
import { connectSocket } from '../../../../store/socketConfigSlice';
import Toast from '../../utils/Toast';
import { completeNotification } from '../../../../store/socketSlice';

const LogDashboard = () => {
  const navigate = useNavigate();
  const { flowId } = useParams();
  const dispatch = useDispatch();
  const socket = useSelector((state) => state.socketConfig.socket);
  const isConnected = useSelector((states) => states.socketConfig.isConnected);
  const userDetail = useSelector((states) => states.user.user);
  const notifyUser = useSelector((states) => states.socket.notification);
  const userId = userDetail?.id;
  const notifyUploadedExported = notifyUser?.isUploadedExported;
  const notificationMessage = notifyUser?.message;
  const DocumentCompletedData = notifyUser?.status;
  const [isLoading, setIsLoading] = useState('');
  const [allFlowData, setAllFlowData] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [flowLogs, setFlowLogs] = useState([]);
  const [selectedRunningFlowData, setSelectedRunningFlowData] = useState([]);
  const [refreshLogs, setRefreshLogs] = useState(false);
  const [showLogs, setShowLogs] = useState(true);
  const [fromDateTime, setFromDateTime] = useState(null);
  const [toDateTime, setToDateTime] = useState(null);
  const [page, setPage] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const logsPerPage = 15; // Adjust the
  const selectedDateFrom = fromDateTime?.$d;
  const selectedDateTo = toDateTime?.$d;
  const runningFlows =
    allFlowData?.length > 0
      ? allFlowData?.filter((flow) => flow?.status === 'Running')
      : [];
  const today = dayjs();

  useEffect(() => {
    if (
      notificationMessage &&
      (DocumentCompletedData === 'Completed' || notifyUploadedExported)
    ) {
      Toast({ message: `${notificationMessage}`, variant: 'success' });

      dispatch(completeNotification({}));
    }
  }, [notificationMessage]);

  useEffect(() => {
    if (isConnected) {
      socket.on('log-dashboard', () => {
        setRefreshLogs((prev) => !prev);
      });
    }
  }, [isConnected]);

  useEffect(() => {
    const response = async () => {
      setIsLoading(true);
      const allFlows = await getAllFlowData();
      if (allFlows) {
        const defaultFlow = allFlows.find((flow) => flow.id === flowId)?.name;
        if (defaultFlow) {
          setSelectedFlow(defaultFlow);
        }
        setAllFlowData(allFlows);
        setIsLoading(false);
      }
    };
    response();
  }, []);

  const fetchFlowLogs = async (page) => {
    const runningFlowData =
      allFlowData?.length > 0
        ? allFlowData.filter((flow) => flow?.name === selectedFlow)
        : [];
    let logs = [];
    setSelectedRunningFlowData(runningFlowData);
    if (runningFlowData?.length > 0) {
      setIsLoading(true);
      const allLogsForRunningFlow = await getFlowLogsById(
        runningFlowData[0]?.id,
        selectedDateFrom,
        selectedDateTo,
        page,
        logsPerPage,
      );
      if (allLogsForRunningFlow) {
        logs = allLogsForRunningFlow?.logsById;
        setIsLoading(false);
      }
    } else {
      setIsLoading(true);
      const allLogsForSelectedFlow = await getFlowLogsById(
        flowId,
        selectedDateFrom,
        selectedDateTo,
        page,
        logsPerPage,
      );
      if (allLogsForSelectedFlow) {
        logs = allLogsForSelectedFlow?.logsById;
        setIsLoading(false);
      }
    }
    if (page === 1) {
      setFlowLogs(logs);
    } else {
      setFlowLogs((prevLogs) => [...prevLogs, ...logs]);
    }

    if (logs.length < logsPerPage) {
      setIsFetchingMore(null); // No more data to fetch
    } else {
      setIsFetchingMore(false); // More data can be fetched
    }
  };

  const handleScroll = (e) => {
    const { target } = e;
    if (
      target.scrollTop + target.clientHeight + 1 >= target.scrollHeight &&
      isFetchingMore != null
    ) {
      setIsFetchingMore(true);
      setPage((prevPage) => prevPage + 1);
    }
  };

  // Add scroll event listener to TableBody

  useEffect(() => {
    if (page > 1) {
      fetchFlowLogs(page);
    }
  }, [page]);
  useEffect(() => {
    // Reset page to 1 when the dependencies change
    setPage(1);
  }, [
    allFlowData,
    selectedFlow,
    flowId,
    refreshLogs,
    selectedDateFrom,
    selectedDateTo,
  ]);

  useEffect(() => {
    if (page === 1) {
      fetchFlowLogs(1);
    }
  }, [
    allFlowData,
    selectedFlow,
    flowId,
    refreshLogs,
    selectedDateFrom,
    selectedDateTo,
  ]);

  useEffect(() => {
    const fetchFlowLogsWithPolling = async () => {
      await fetchFlowLogs(page);
    };

    const pollingInterval = setInterval(fetchFlowLogsWithPolling, 5000);

    return () => clearInterval(pollingInterval); // Clear polling interval on cleanup
  }, [allFlowData, selectedFlow, flowId, selectedDateFrom, selectedDateTo]);

  const handleRunningFlow = (event) => {
    setSelectedFlow(event.target.value);
  };
  const handleRefreshLogs = () => {
    setRefreshLogs((prev) => !prev);
    setShowLogs(true);
  };

  const calculateTimeAgo = (UTCTime) => {
    const logTime = moment(UTCTime);
    const currentTime = moment();
    const diff = currentTime.diff(logTime, 'minutes'); // Calculate the difference in minutes

    if (diff < 1) {
      return 'now';
    }
    if (diff < 60) {
      return `${diff} mins ago `;
    }
    if (diff < 1440) {
      return `${Math.floor(diff / 60)} hours ago`;
    }
    if (diff < 10080) {
      return `${Math.floor(diff / 1440)} days ago`;
    }
    return `${Math.floor(diff / 10080)} weeks ago`;
  };

  useEffect(() => {
    if (runningFlows?.length > 0) {
      const runningFlow = runningFlows?.filter(
        (flow) => flow?.status === 'Running',
      );
      if (userId && !isConnected && runningFlow?.length > 0) {
        dispatch(connectSocket(userId));
      } else if (runningFlow?.length === 0 && isConnected) {
        // dispatch(disconnectSocket());
      }
    }
  }, [dispatch, runningFlows, userId, isConnected]);

  useEffect(
    () => () => {
      if (isConnected) {
        // dispatch(disconnectSocket());
      }
    },
    [dispatch, isConnected],
  );

  return (
    <Root className={classes.root}>
      <Box className={classes.mainLogsBox}>
        <Box className={classes.leftLogsBox}>
          {selectedFlow && (
            <Breadcrumbs>
              <Link
                underline="hover"
                href="/flow-configuration"
                onClick={() => navigate('/flow-configuration')}
              >
                <Typography variant="h4" color={lightPalette.darkPurple.main}>
                  Flows
                </Typography>
              </Link>
              <Link
                underline="hover"
                href={
                  selectedRunningFlowData
                    ? `/flow-configuration/editor?flowId=${selectedRunningFlowData[0]?.id}`
                    : `/flow-configuration/editor?flowId=${flowId}`
                }
                onClick={() =>
                  navigate(
                    selectedRunningFlowData
                      ? `/flow-configuration/editor?flowId=${selectedRunningFlowData[0]?.id}`
                      : `/flow-configuration/editor?flowId=${flowId}`,
                  )
                }
              >
                <Typography variant="h4" color={lightPalette.darkPurple.main}>
                  {selectedFlow || selectedRunningFlowData}
                </Typography>
              </Link>
              <Typography variant="h4" color={lightPalette.darkPurple.main}>
                Logs
              </Typography>
            </Breadcrumbs>
          )}
        </Box>
        <Box className={classes.rightLogsBox}>
          <Select
            className={classes.selectForLogs}
            size="small"
            IconComponent={KeyboardArrowDownIcon}
            renderValue={(selected) => {
              if (!selected || selected.length === 0) {
                return (
                  <Typography variant="body1" color={theme.palette.grey[500]}>
                    Select Flow Name
                  </Typography>
                );
              }

              return <Typography>{selected}</Typography>;
            }}
            value={selectedFlow}
            onChange={handleRunningFlow}
            displayEmpty
          >
            {runningFlows?.length > 0 ? (
              runningFlows?.map((flow) => (
                <MenuItem key={flow?.id} value={flow?.name}>
                  {flow?.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No running flows available</MenuItem>
            )}
          </Select>
          <Box className={classes.dateTimePickerBox}>
            <Typography
              variant="body1"
              className={classes.dateTimePickerTypography}
            >
              From :{' '}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                clearable
                maxDate={today}
                slotProps={{
                  field: {
                    clearable: true,
                    size: 'small',
                    error: false,
                  },
                  textField: {
                    inputProps: {
                      readOnly: true,
                      placeholder: 'Select Date & Time',
                    },
                  },
                }}
                value={fromDateTime || null}
                onChange={(newValue) => setFromDateTime(newValue)}
              />
            </LocalizationProvider>
            <Typography
              variant="body1"
              className={classes.dateTimePickerTypography}
            >
              To :{' '}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                minDateTime={fromDateTime || null}
                maxDate={today}
                clearable
                slotProps={{
                  field: {
                    clearable: true,
                    size: 'small',
                    error: false,
                  },
                  textField: {
                    inputProps: {
                      readOnly: true,
                      placeholder: 'Select Date & Time',
                    },
                  },
                }}
                value={toDateTime || null}
                onChange={(newValue) => setToDateTime(newValue)}
              />
            </LocalizationProvider>
          </Box>
          <CustomTooltip title="Refresh logs">
            <Button
              variant="contained"
              onClick={handleRefreshLogs}
              className={classes.actionButton}
            >
              <RefreshIcon />
            </Button>
          </CustomTooltip>

          <CustomTooltip title="Cache logs">
            <Button
              variant="contained"
              onClick={() => setShowLogs(false)}
              className={classes.actionButton}
            >
              <BlockIcon />
            </Button>
          </CustomTooltip>
        </Box>
      </Box>
      <TableContainer
        component={Paper}
        onScroll={handleScroll}
        id="table-container"
        className={classes.tableContainerForLogs}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeaderForLogsDescription}>
                Description
              </TableCell>
              <TableCell className={classes.tableHeaderForLogsTime}>
                Time
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showLogs && flowLogs?.length > 0 ? (
              <>
                {flowLogs.map((log) => (
                  <TableRow key={log?.name}>
                    <TableCell component="th" scope="row">
                      <Box className={classes.tableCellContentBox}>
                        <SquareIcon
                          className={
                            log?.logs?.label === 'success'
                              ? classes.squareIconSuccess
                              : classes.squareIconError
                          }
                        />
                        <Typography
                          className={classes.tableCellContentBoxTypography}
                        >
                          {typeof log?.logs?.data === 'object'
                            ? JSON.stringify(log?.logs?.data)
                            : log?.logs?.data}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{calculateTimeAgo(log?.created_at)}</TableCell>
                  </TableRow>
                ))}
              </>
            ) : (
              <TableRow>
                <TableCell align="center" colSpan={2}>
                  {isLoading && (selectedFlow || selectedRunningFlowData) ? (
                    <CircularProgress />
                  ) : (
                    'No Logs Found'
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Root>
  );
};

export default LogDashboard;
